import React, { useEffect, useState } from 'react';
import { GIButton, Modal } from 'app-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { CannedCategory } from '../../../constants/common/CannedCategory';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';

const CustomModalHeader = styled(Modal.Header)`
    background-color: ${({ theme }) => theme.gray8};
`;

const StyledModal = styled.div`
    .gi-modal__body {
        margin-bottom: 10px;
    }
    .template-form__input{
        margin-bottom:0 !important
    }
`;
const DESCRIPTION_LENGTH = {
    min: 5,
    max: 2000,
};
const TemplateModal = props => {
    const {
        modalData: {
            addNewTemplate,
            updateTemplate,
            isEdit,
            initialFormValues,
            headerText,
            onClose,
            submitBtnText,
            templateCategoryName,
        },
        errorMessage,
        showErrorMessage,
        onErrorStatusChange
    } = props;
    const intl = useIntl();
    const [content, setContent] = useState('');
    const [templateName, setTemplateName] = useState('');
    const [subject, setSubject] = useState('');
    const [isCategorySendMessage, setIsCategorySendMessage] = useState(false);
    const [enableAsActive, setEnableAsActive] = useState('');
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({ defaultValues: initialFormValues, mode: 'onChange' });

    useEffect(() => {
        if (!isEdit) {
            setValue('values', '');
        }
    }, [isEdit]);

    useEffect(()=>{
        setIsCategorySendMessage(templateCategoryName !== CannedCategory.INTERNAL_COMMENT);
    },[templateCategoryName]);

    const onSubmit = data => {
        isEdit ? updateTemplate(data) : addNewTemplate(data);
    };

    const handleCancel = e => {
        e.preventDefault();
        onClose();
    };
    
    const handleOkButtonClick = () =>{
        onErrorStatusChange();
    };

    const isButtonEnable = () => {
        const isTemplateEmpty = !templateName.trim();
        const isSubjectEmpty = !subject.trim();
        const isContentEmpty = !content.trim();
        const isInternal = templateCategoryName === CannedCategory.INTERNAL_COMMENT;
        const isEnableAsActive = !enableAsActive.trim();
        if (isInternal) {
            return isEdit
                ? isTemplateEmpty && isContentEmpty && isEnableAsActive
                : isTemplateEmpty || isContentEmpty ;
        }
    
        return isEdit
            ? isTemplateEmpty && isSubjectEmpty && isContentEmpty && isEnableAsActive
            : isTemplateEmpty || isSubjectEmpty || isContentEmpty ;
    };

    return (
        <StyledModal>
            <Modal id="template_modal">
                <CustomModalHeader id="template_modal_title" titleId={headerText} onHide={handleCancel} />
                <Modal.Body>
                    <form className="template-form" id="ticketForm" onSubmit={handleSubmit(onSubmit)}>
                        <ConditionalRendering isTrue={errorMessage && showErrorMessage}>
                            <div className='template-api-error'>
                                <div className="template-mandatory">{errorMessage}</div>
                            </div>
                            <div className='template-api-error'>
                                <GIButton
                                    dataTestId="template_error_ok_button"
                                    id="templateResponseError"
                                    labelId={'button.ok'}
                                    onClickHandler={handleOkButtonClick}
                                /></div>
                        </ConditionalRendering>
                        <label className="template-form__label">
                            <FormattedMessage id="ticket_management.template_form.form_header.name" />
                            <span className="template-mandatory">*</span>
                            <input
                                type="text"
                                className="template-form__input"
                                name="templateName"
                                data-testid="canned_response_template_name"
                                maxLength={100}
                                autoFocus={isEdit}
                                placeholder={intl.formatMessage({
                                    id: 'ticket_management.templateNamePlaceHolder',
                                })}
                                {...register('templateName', {
                                    required: {
                                        value: true,
                                        message: intl.formatMessage({
                                            id: 'ticket_management.template_form.form_header.name_validation_error',
                                        }),
                                    },
                                    onChange: e => {
                                        e.target.value.length <= DESCRIPTION_LENGTH.max &&
                                            setTemplateName(e.target.value);
                                    },
                                })}
                            />
                        </label>
                        <ConditionalRendering isTrue={errors.templateName}>
                            <p className="shown_template error--msg">{errors.templateName?.message}</p>
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isCategorySendMessage}>
                            <label className="template-form__label">
                                <FormattedMessage id="ticket_management.template_form.form_header.subject" />
                                <span className="template-mandatory">*</span>
                                <input
                                    type="text"
                                    className={'template-form__input'}
                                    name="templateSubject"
                                    data-testid="canned_response_template_subject"
                                    maxLength={200}
                                    placeholder={intl.formatMessage({
                                        id: 'ticket_management.templateSubjectPlaceHolder',
                                    })}
                                    {...register('templateSubject', {
                                        required: {
                                            value: isCategorySendMessage,
                                            message: intl.formatMessage({
                                                id: 'ticket_management.template_form.form_header.subject_validation_error',
                                            }),
                                        },
                                        onChange: e => {
                                            e.target.value.length <= DESCRIPTION_LENGTH.max &&
                                                setSubject(e.target.value);
                                        },
                                    })}
                                />
                            </label>
                        </ConditionalRendering>
                        <ConditionalRendering
                            isTrue={isCategorySendMessage && errors.templateSubject}
                        >
                            <p className="shown_template error--msg">{errors.templateSubject?.message}</p>
                        </ConditionalRendering>
                        <label className="template-form__label">
                            <FormattedMessage id="ticket_management.template_form.form_header.content" />
                            <span className="template-mandatory">*</span>
                            <textarea
                                type="text"
                                className={'template-form__input'}
                                name="templateContent"
                                data-testid="canned_response_template_content"
                                rows={5}
                                placeholder={intl.formatMessage({
                                    id: 'ticket_management.templateContentPlaceHolder',
                                })}
                                {...register('templateContent', {
                                    required: {
                                        value: true,
                                        message: intl.formatMessage({
                                            id: 'ticket_management.template_form.form_header.content_validation_error',
                                        }),
                                    },
                                    onChange: e => {
                                        e.target.value.length <= DESCRIPTION_LENGTH.max && setContent(e.target.value);
                                    },
                                    maxLength: {
                                        value: DESCRIPTION_LENGTH.max,
                                        message: (
                                            <FormattedMessage id="ticket_management.template_form.form_header.content.maxlength" />
                                        ),
                                    },
                                })}
                            />
                        </label>
                        <ConditionalRendering isTrue={!errors.templateContent}>
                            <div data-testid="canned_response_template_form_modal_max_chars_left" >
                                {DESCRIPTION_LENGTH.max - (content?.trim().length || initialFormValues.templateContent.trim().length)}{' '}
                                <FormattedMessage id="cap.applications.modal.charactersLeft" />
                            </div>
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={errors.templateContent}>
                            <p className="shown_template error--msg">{errors.templateContent?.message}</p>
                        </ConditionalRendering>
                        <label className="template-form__label" style={{ display: 'flex', alignItems: 'center' }}>
                            <FormattedMessage id="ticket_management.template_form.form_header.activate" />
                            <input
                                name="enableAsActive"
                                type="checkbox"
                                data-testid="canned_response_enable_as_active"
                                className="margin-left-1em"
                                {...register('enableAsActive', {onChange:e=>{setEnableAsActive(e.target.value);}})}
                            />
                        </label>
                    </form>
                </Modal.Body>
                <Modal.Controls>
                    <div
                        className="template-form__controls"
                        data-testid="canned_response_template_form_modal_controls"
                    >
                        <GIButton
                            dataTestId="canned_response_template_modal_controls_cancel_button"
                            id="canned_response_template_modal_controls_cancel_button"
                            labelId="button.cancel"
                            onClickHandler={handleCancel}
                            variant="secondary"
                        />
                        <GIButton
                            dataTestId="canned_response_template_modal_controls_submit_update_button"
                            id="canned_response_template_modal_controls_submit_update_button"
                            labelId={submitBtnText}
                            isDisabled={isButtonEnable()}
                            onClickHandler={handleSubmit(onSubmit)}
                        />
                    </div>
                </Modal.Controls>
            </Modal>
        </StyledModal>
    );
};

export default TemplateModal;
